import type { PageType } from 'root/widgets/types';

export enum PageOrigin {
  NEW_LAUNCH_NAME = 'New Launches Directory',
  OTHERS = 'Others',
  SEARCH_ENGINE = 'Search Engine',
}

enum UrlMatchVariants {
  EXACT = 'exact',
  INCLUDE = 'include',
  REGEX = 'regex',
}

export const SEARCH_ENGINES = ['google', 'bing', 'yahoo', 'duckduckgo', 'baidu', 'yandex'];

/**
 * Safely create a URL object from a referrer.
 *
 * @param referrer - The document referrer
 * @returns the URL object if the referrer is valid, otherwise undefined
 */
export const getSafeUrl = (referrer: string): URL | undefined => {
  try {
    return new URL(referrer);
  } catch {
    return undefined;
  }
};

/**
 * Check if the hostname is from a search engine.
 *
 * @param hostname - The hostname to check
 * @returns true if the hostname is from a search engine, otherwise false
 */
export const isSearchEngine = (hostname: string): boolean =>
  SEARCH_ENGINES.some((searchEngine) => hostname.includes(searchEngine));

/**
 * Helper to get the PageTypes,
 * If the referrer is empty or contains any of the search engines hostname, it will return OTHERS.
 * Otherwise, it will return the corresponding page type based on the config mapping.
 * For example '/property-for-sale' is 'Listing Search' pageTypes.
 * It first get all the lists urlPageTypesMapping from config repo,
 * and search for the pageTypes from the document.referrer we currently have.
 *
 * @param referrer - The referrer
 * @param pageTypes - The available page types
 * @returns the corresponding page type if found, otherwise empty string
 */
export const getOriginPageType = (referrer: string, pageTypes?: Array<PageType>): string => {
  // when referrer is empty, it means the user is directly accessing the page
  if (referrer === '') {
    return PageOrigin.OTHERS;
  }

  const url = getSafeUrl(referrer);

  if (!pageTypes?.length || !url) {
    return '';
  }

  if (isSearchEngine(url.hostname)) {
    return PageOrigin.SEARCH_ENGINE;
  }

  const path = decodeURI(url.pathname);

  const originPageResult: string =
    pageTypes.find((item) => {
      switch (item.match) {
        case UrlMatchVariants.EXACT:
          return path === item.path;
        case UrlMatchVariants.INCLUDE:
          return path.includes(item.path);
        case UrlMatchVariants.REGEX:
          return path.match(item.path);
        default:
          return false;
      }
    })?.originPage || '';

  return originPageResult;
};
